import { useSelector } from 'react-redux';
import { OrderNowCTA } from '../orderNowCTA';
import { DonateToSocialCause } from '@/components/account/rewards/donate-to-social-cause/donate-to-social-cause';
import { RedeemInStoreModal } from '@/components/account/rewards/redeem-in-store-modal/redeem-in-store-modal';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { Modal } from '@/components/uielements/modal';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import {
  StyledBankedCash,
  StyledBankedCashContent,
  StyledBorderBottom,
  StyledButtonContainer,
  StyledCashButtonContainer,
  StyledRewardsHeader,
  StyledRewardsHeaderContainer,
} from '@/components/uielements/reward/styles';
import { PROGRAM_TYPES } from '@/constants/loyalty';
import { meSliceSelector } from '@/redux/me/reducer';
import { organizationSliceSelector } from '@/redux/organization/reducer';
import { toDollars } from '@/utils/basket';
import { dateFormatter } from '@/utils/loyalty';

export const BankedCurrency = () => {
  const { loyaltyState } = useSelector(meSliceSelector);
  const organization = useSelector(organizationSliceSelector);

  const bankedCurrency = loyaltyState?.banked_currency;
  const expirationDate = loyaltyState?.banked_currency_expiration_date;
  const currencyExpiring = loyaltyState?.banked_currency_expiring;

  // Punchh program types
  const programType = organization?.loyalty.program_type;

  // Only render component if 'Currency' reward program
  if (programType !== PROGRAM_TYPES.POINTS_CONVERT_CURRENCY) {
    return null;
  }

  return (
    <>
      <StyledRewardsHeaderContainer>
        <StringAccessor
          tag={StyledRewardsHeader}
          accessor="rewards.banked_currency_header"
          html={true}
        />
        <StringAccessor tag="p" accessor="rewards.banked_currency_subheader" html={true} />
        <StyledBorderBottom />
      </StyledRewardsHeaderContainer>
      <StyledBankedCash>
        <StyledBankedCashContent>
          <StringAccessor tag="h3" accessor="rewards.banked_currency_card_header" html={true} />
          <h1>${toDollars(bankedCurrency)}</h1>

          {/* If banked currency is $0, display prompt to order now */}
          {!bankedCurrency ? (
            <StringAccessor
              tag="p"
              accessor="rewards.banked_currency_empty_state_text"
              html={true}
            />
          ) : (
            // else if there is banked currency, expiration date, AND currency expiring, display the expiration date
            expirationDate &&
            currencyExpiring &&
            currencyExpiring > 0 && (
              <StringAccessor
                tag="p"
                accessor="rewards.banked_currency_expiration_text"
                html={true}
                dataObj={{
                  currency: `$${toDollars(currencyExpiring)}`,
                  date: dateFormatter(expirationDate),
                }}
              />
            )
          )}
        </StyledBankedCashContent>

        <StyledCashButtonContainer>
          <OrderNowCTA label="Rewards - Rewards" />

          {/* Only display Redeem in Store if banked currency is more than $0 */}
          {!!bankedCurrency && (
            <>
              <Modal.Container
                trigger={(open) => (
                  <StyledPrimaryButton
                    data-css-override="StyledPrimaryButton"
                    onClick={() => open()}
                    $buttonTheme="knockout"
                    aria-label="Redeem banked currency in store"
                  >
                    <StringAccessor accessor="rewards.redeem_in_store_button" />
                  </StyledPrimaryButton>
                )}
              >
                {(toggle) => <RedeemInStoreModal onClose={toggle} initialAmount={bankedCurrency} />}
              </Modal.Container>
            </>
          )}
        </StyledCashButtonContainer>

        {!!bankedCurrency ? (
          <StyledButtonContainer>
            <DonateToSocialCause
              donation={{
                type: 'currency',
                item: bankedCurrency.toString(),
              }}
              rewardName={'Banked Currency'}
            />
          </StyledButtonContainer>
        ) : null}
      </StyledBankedCash>
    </>
  );
};
